import { useEffect, useState } from 'react';
import type { OfferteProduct } from '../../lib/offertes';
import { updateLeadProduct, type KleedVorm } from '../../lib/lead-product';
import { isCountBasedSku } from '../../lib/offerte-products';

interface EditKleedDialogProps {
  isOpen: boolean;
  leadProduct: OfferteProduct | null;
  onClose: () => void;
  onSaved: () => void;
}

function inferVorm(p: OfferteProduct): KleedVorm {
  if (p.diameterCm && p.diameterCm > 0) return 'rond';
  if (p.lengthCm && p.widthCm && p.lengthCm > 0 && p.widthCm > 0) return 'rechthoekig';
  return 'overig';
}

export function EditKleedDialog({ isOpen, leadProduct, onClose, onSaved }: EditKleedDialogProps) {
  const [lengte, setLengte] = useState<string>('');
  const [breedte, setBreedte] = useState<string>('');
  const [diameter, setDiameter] = useState<string>('');
  const [oppervlakte, setOppervlakte] = useState<string>('');
  const [bijzonderheden, setBijzonderheden] = useState<string>('');
  const [externeReferentie, setExterneReferentie] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !leadProduct) return;
    setLengte(leadProduct.lengthCm ? String(leadProduct.lengthCm) : '');
    setBreedte(leadProduct.widthCm ? String(leadProduct.widthCm) : '');
    setDiameter(leadProduct.diameterCm ? String(leadProduct.diameterCm) : '');
    setOppervlakte(leadProduct.oppervlakteM2 ? String(leadProduct.oppervlakteM2) : '');
    setBijzonderheden(leadProduct.bijzonderheden || '');
    setExterneReferentie(leadProduct.externeReferentie || '');
    setError(null);
  }, [isOpen, leadProduct]);

  if (!isOpen || !leadProduct) return null;

  const vorm = inferVorm(leadProduct);
  const isCountBased = isCountBasedSku(leadProduct.productCode);

  const initial = {
    lengte: leadProduct.lengthCm ? String(leadProduct.lengthCm) : '',
    breedte: leadProduct.widthCm ? String(leadProduct.widthCm) : '',
    diameter: leadProduct.diameterCm ? String(leadProduct.diameterCm) : '',
    oppervlakte: leadProduct.oppervlakteM2 ? String(leadProduct.oppervlakteM2) : '',
    bijzonderheden: leadProduct.bijzonderheden || '',
    externeReferentie: leadProduct.externeReferentie || '',
  };

  const dimensionsChanged =
    lengte !== initial.lengte ||
    breedte !== initial.breedte ||
    diameter !== initial.diameter ||
    oppervlakte !== initial.oppervlakte;
  const metaChanged =
    bijzonderheden !== initial.bijzonderheden ||
    externeReferentie !== initial.externeReferentie;
  // Voor count-based items zijn afmeting-velden niet relevant — alleen meta-changes tellen.
  const hasChanges = isCountBased ? metaChanged : dimensionsChanged || metaChanged;

  async function handleSave() {
    if (!leadProduct) return;
    setSaving(true);
    setError(null);
    const payload: Parameters<typeof updateLeadProduct>[0] = {
      leadProductId: leadProduct.id,
    };
    if (!isCountBased) {
      if (vorm === 'rechthoekig') {
        if (lengte !== initial.lengte) payload.lengteCm = lengte ? Number(lengte) : null;
        if (breedte !== initial.breedte) payload.breedteCm = breedte ? Number(breedte) : null;
      } else if (vorm === 'rond') {
        if (diameter !== initial.diameter) payload.diameterCm = diameter ? Number(diameter) : null;
      } else {
        if (oppervlakte !== initial.oppervlakte) {
          payload.oppervlakteM2 = oppervlakte ? Number(oppervlakte) : null;
        }
      }
    }
    if (bijzonderheden !== initial.bijzonderheden) {
      payload.bijzonderheden = bijzonderheden;
    }
    if (externeReferentie !== initial.externeReferentie) {
      payload.externeReferentie = externeReferentie;
    }
    try {
      const res = await updateLeadProduct(payload);
      if (!res.success) {
        setError(res.message || 'Opslaan mislukt');
        return;
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Kleed bewerken</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {leadProduct.productName || leadProduct.productCode}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {isCountBased && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Aantal: <span className="font-medium text-slate-800">{leadProduct.quantity || 0}</span> stuks
              <div className="mt-0.5 text-slate-500">
                Aantal wijzigen kan niet — verwijder en voeg opnieuw toe als het aantal anders is.
              </div>
            </div>
          )}

          {!isCountBased && vorm === 'rechthoekig' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-600">Lengte (cm)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={lengte}
                  onChange={(e) => setLengte(e.target.value)}
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Breedte (cm)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={breedte}
                  onChange={(e) => setBreedte(e.target.value)}
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
            </div>
          )}

          {!isCountBased && vorm === 'rond' && (
            <label className="block">
              <span className="text-xs text-slate-600">Diameter (cm)</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          )}

          {!isCountBased && vorm === 'overig' && (
            <label className="block">
              <span className="text-xs text-slate-600">Oppervlakte (m²)</span>
              <input
                type="number"
                inputMode="decimal"
                min={0.01}
                step={0.01}
                value={oppervlakte}
                onChange={(e) => setOppervlakte(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          )}

          <label className="block">
            <span className="text-xs text-slate-600">Bijzonderheden</span>
            <textarea
              rows={3}
              maxLength={1000}
              value={bijzonderheden}
              onChange={(e) => setBijzonderheden(e.target.value)}
              placeholder="Bijv. randen iets gerafeld, vlek bij het midden"
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
            />
          </label>

          <label className="block">
            <span className="text-xs text-slate-600">Externe referentie</span>
            <input
              type="text"
              maxLength={80}
              value={externeReferentie}
              onChange={(e) => setExterneReferentie(e.target.value)}
              placeholder="Bijv. museum-objectnr 002.523, claimnummer"
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
            />
          </label>

          {!isCountBased && (
            <p className="text-xs text-slate-400">
              Vorm wisselen kan niet — verwijder en voeg opnieuw toe als de vorm anders is.
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
